/* istanbul ignore file -- This file is loaded only in Atlaspack builds (guarded
   by the `if (__ATLASPACK__)` branch in editor-translation-util.tsx). Jest does
   not define `__ATLASPACK__`, so it never requires this file from the main
   module. Coverage for this file is collected via its dedicated test file
   (editor-translation-util.atlaspack.test.tsx) when run in isolation. */
import type { MessageFormatElement } from '@formatjs/icu-messageformat-parser';

/**
 * Atlaspack-only loader for editor translation chunks.
 *
 * This file is loaded via `require('./editor-translation-util.atlaspack')`
 * from the main `editor-translation-util.tsx`, but only inside a branch
 * guarded by the `__ATLASPACK__` build-time constant. Webpack inlines
 * `__ATLASPACK__` as `false` and statically eliminates the entire `require`
 * call, so it never tries to resolve any of the literal `import()` specifiers
 * below — including the `@atlaskit/<pkg>/i18n/<locale>` paths that break on
 * webpack because the platform-resolver maps the package name to
 * `src/index.ts` and then prefix-matches the subpath onto that file.
 *
 * Atlaspack inlines `__ATLASPACK__` as `true` and resolves all literal
 * `import('@atlaskit/<pkg>/i18n/<locale>')` calls via standard
 * `package.json#exports` field resolution — each exposes `./i18n/*` →
 * `./src/i18n/*.ts`.
 *
 * Why a literal lookup table rather than `import(\`@atlaskit/.../${locale}\`)`:
 *  - Atlaspack only emits async bundles for `import('literal-string')` —
 *    it does not implement webpack's directory-context glob fallback for
 *    template-literal `import()` calls. A template literal therefore
 *    produces zero chunks on atlaspack and the returned promise never
 *    resolves, leaving the editor in an infinite load state.
 *  - Confluence's `next/packages/i18n/src/messages.ts` is the canonical AFM
 *    example of this pattern (switch-style file with one literal `import()`
 *    per package per locale). We mirror it here.
 *
 * Keys are the **already-normalised editor-package locale code** that
 * `importLocaleMessages` receives (i.e. the output of `getNormalizedLocale`
 * → `getLanguageFromLocale`, e.g. `en`, `en_GB`, `pt_BR`, `nb`, `fr`, `zh`).
 *
 * Each importer returns one literal `import()` per editor package. Some
 * `@atlaskit/editor-common` and `@atlaskit/media-ui` locales are
 * intentionally omitted because the file does not exist upstream
 * (`is`, `ro`). Serbian (`sr` / `sr_RS` / `sr_YR`) is intentionally
 * unsupported and falls through to the `en` entry.
 *
 * Unknown locales fall through to the `en` entry below, matching
 * Confluence's behaviour and what `<IntlProvider>` does for unrecognised
 * locales.
 *
 * To add a locale: copy an existing entry, change every literal locale
 * code, and add it under the appropriate normalised key. Do NOT replace
 * the literal specifiers with template literals — atlaspack will silently
 * drop the chunks.
 */
type EditorMessages =
	| Record<string, string>
	| Record<string, MessageFormatElement[]>
	| Record<string, never>;

interface EditorModule {
	default: EditorMessages;
}

type EditorMessageModule = EditorModule | undefined;
type EditorTranslationImporter = () => Promise<EditorMessageModule>[];

const EDITOR_TRANSLATION_IMPORTERS: Record<string, EditorTranslationImporter> = {
	cs: () => [
		import(/* webpackChunkName: "editor-translations-cs" */ '@atlaskit/editor-core/i18n/cs'),
		import(/* webpackChunkName: "editor-translations-cs" */ '@atlaskit/emoji/i18n/cs'),
		import(/* webpackChunkName: "editor-translations-cs" */ '@atlaskit/editor-common/i18n/cs'),
		import(/* webpackChunkName: "editor-translations-cs" */ '@atlaskit/media-ui/i18n/cs'),
		import(/* webpackChunkName: "editor-translations-cs" */ '@atlaskit/smart-card/i18n/cs'),
	],
	da: () => [
		import(/* webpackChunkName: "editor-translations-da" */ '@atlaskit/editor-core/i18n/da'),
		import(/* webpackChunkName: "editor-translations-da" */ '@atlaskit/emoji/i18n/da'),
		import(/* webpackChunkName: "editor-translations-da" */ '@atlaskit/editor-common/i18n/da'),
		import(/* webpackChunkName: "editor-translations-da" */ '@atlaskit/media-ui/i18n/da'),
		import(/* webpackChunkName: "editor-translations-da" */ '@atlaskit/smart-card/i18n/da'),
	],
	de: () => [
		import(/* webpackChunkName: "editor-translations-de" */ '@atlaskit/editor-core/i18n/de'),
		import(/* webpackChunkName: "editor-translations-de" */ '@atlaskit/emoji/i18n/de'),
		import(/* webpackChunkName: "editor-translations-de" */ '@atlaskit/editor-common/i18n/de'),
		import(/* webpackChunkName: "editor-translations-de" */ '@atlaskit/media-ui/i18n/de'),
		import(/* webpackChunkName: "editor-translations-de" */ '@atlaskit/smart-card/i18n/de'),
	],
	en: () => [
		import(/* webpackChunkName: "editor-translations-en" */ '@atlaskit/editor-core/i18n/en'),
		import(/* webpackChunkName: "editor-translations-en" */ '@atlaskit/emoji/i18n/en'),
		import(/* webpackChunkName: "editor-translations-en" */ '@atlaskit/editor-common/i18n/en'),
		import(/* webpackChunkName: "editor-translations-en" */ '@atlaskit/media-ui/i18n/en'),
		import(/* webpackChunkName: "editor-translations-en" */ '@atlaskit/smart-card/i18n/en'),
	],
	en_GB: () => [
		import(/* webpackChunkName: "editor-translations-en_GB" */ '@atlaskit/editor-core/i18n/en_GB'),
		import(/* webpackChunkName: "editor-translations-en_GB" */ '@atlaskit/emoji/i18n/en_GB'),
		import(
			/* webpackChunkName: "editor-translations-en_GB" */ '@atlaskit/editor-common/i18n/en_GB'
		),
		import(/* webpackChunkName: "editor-translations-en_GB" */ '@atlaskit/media-ui/i18n/en_GB'),
		import(/* webpackChunkName: "editor-translations-en_GB" */ '@atlaskit/smart-card/i18n/en_GB'),
	],
	es: () => [
		import(/* webpackChunkName: "editor-translations-es" */ '@atlaskit/editor-core/i18n/es'),
		import(/* webpackChunkName: "editor-translations-es" */ '@atlaskit/emoji/i18n/es'),
		import(/* webpackChunkName: "editor-translations-es" */ '@atlaskit/editor-common/i18n/es'),
		import(/* webpackChunkName: "editor-translations-es" */ '@atlaskit/media-ui/i18n/es'),
		import(/* webpackChunkName: "editor-translations-es" */ '@atlaskit/smart-card/i18n/es'),
	],
	et: () => [
		import(/* webpackChunkName: "editor-translations-et" */ '@atlaskit/editor-core/i18n/et'),
		import(/* webpackChunkName: "editor-translations-et" */ '@atlaskit/emoji/i18n/et'),
		import(/* webpackChunkName: "editor-translations-et" */ '@atlaskit/editor-common/i18n/et'),
		import(/* webpackChunkName: "editor-translations-et" */ '@atlaskit/media-ui/i18n/et'),
		import(/* webpackChunkName: "editor-translations-et" */ '@atlaskit/smart-card/i18n/et'),
	],
	fi: () => [
		import(/* webpackChunkName: "editor-translations-fi" */ '@atlaskit/editor-core/i18n/fi'),
		import(/* webpackChunkName: "editor-translations-fi" */ '@atlaskit/emoji/i18n/fi'),
		import(/* webpackChunkName: "editor-translations-fi" */ '@atlaskit/editor-common/i18n/fi'),
		import(/* webpackChunkName: "editor-translations-fi" */ '@atlaskit/media-ui/i18n/fi'),
		import(/* webpackChunkName: "editor-translations-fi" */ '@atlaskit/smart-card/i18n/fi'),
	],
	fr: () => [
		import(/* webpackChunkName: "editor-translations-fr" */ '@atlaskit/editor-core/i18n/fr'),
		import(/* webpackChunkName: "editor-translations-fr" */ '@atlaskit/emoji/i18n/fr'),
		import(/* webpackChunkName: "editor-translations-fr" */ '@atlaskit/editor-common/i18n/fr'),
		import(/* webpackChunkName: "editor-translations-fr" */ '@atlaskit/media-ui/i18n/fr'),
		import(/* webpackChunkName: "editor-translations-fr" */ '@atlaskit/smart-card/i18n/fr'),
	],
	hr: () => [
		import(/* webpackChunkName: "editor-translations-hr" */ '@atlaskit/editor-core/i18n/hr'),
		import(/* webpackChunkName: "editor-translations-hr" */ '@atlaskit/emoji/i18n/hr'),
		import(/* webpackChunkName: "editor-translations-hr" */ '@atlaskit/editor-common/i18n/hr'),
		import(/* webpackChunkName: "editor-translations-hr" */ '@atlaskit/media-ui/i18n/hr'),
		import(/* webpackChunkName: "editor-translations-hr" */ '@atlaskit/smart-card/i18n/hr'),
	],
	hu: () => [
		import(/* webpackChunkName: "editor-translations-hu" */ '@atlaskit/editor-core/i18n/hu'),
		import(/* webpackChunkName: "editor-translations-hu" */ '@atlaskit/emoji/i18n/hu'),
		import(/* webpackChunkName: "editor-translations-hu" */ '@atlaskit/editor-common/i18n/hu'),
		import(/* webpackChunkName: "editor-translations-hu" */ '@atlaskit/media-ui/i18n/hu'),
		import(/* webpackChunkName: "editor-translations-hu" */ '@atlaskit/smart-card/i18n/hu'),
	],
	is: () => [
		// Mirrors confluence/next/packages/i18n/src/messages.ts: omit
		// @atlaskit/editor-common and @atlaskit/media-ui — neither ships `is`.
		import(/* webpackChunkName: "editor-translations-is" */ '@atlaskit/editor-core/i18n/is'),
		import(/* webpackChunkName: "editor-translations-is" */ '@atlaskit/emoji/i18n/is'),
		import(/* webpackChunkName: "editor-translations-is" */ '@atlaskit/smart-card/i18n/is'),
	],
	it: () => [
		import(/* webpackChunkName: "editor-translations-it" */ '@atlaskit/editor-core/i18n/it'),
		import(/* webpackChunkName: "editor-translations-it" */ '@atlaskit/emoji/i18n/it'),
		import(/* webpackChunkName: "editor-translations-it" */ '@atlaskit/editor-common/i18n/it'),
		import(/* webpackChunkName: "editor-translations-it" */ '@atlaskit/media-ui/i18n/it'),
		import(/* webpackChunkName: "editor-translations-it" */ '@atlaskit/smart-card/i18n/it'),
	],
	ja: () => [
		import(/* webpackChunkName: "editor-translations-ja" */ '@atlaskit/editor-core/i18n/ja'),
		import(/* webpackChunkName: "editor-translations-ja" */ '@atlaskit/emoji/i18n/ja'),
		import(/* webpackChunkName: "editor-translations-ja" */ '@atlaskit/editor-common/i18n/ja'),
		import(/* webpackChunkName: "editor-translations-ja" */ '@atlaskit/media-ui/i18n/ja'),
		import(/* webpackChunkName: "editor-translations-ja" */ '@atlaskit/smart-card/i18n/ja'),
	],
	ko: () => [
		import(/* webpackChunkName: "editor-translations-ko" */ '@atlaskit/editor-core/i18n/ko'),
		import(/* webpackChunkName: "editor-translations-ko" */ '@atlaskit/emoji/i18n/ko'),
		import(/* webpackChunkName: "editor-translations-ko" */ '@atlaskit/editor-common/i18n/ko'),
		import(/* webpackChunkName: "editor-translations-ko" */ '@atlaskit/media-ui/i18n/ko'),
		import(/* webpackChunkName: "editor-translations-ko" */ '@atlaskit/smart-card/i18n/ko'),
	],
	nb: () => [
		import(/* webpackChunkName: "editor-translations-nb" */ '@atlaskit/editor-core/i18n/nb'),
		import(/* webpackChunkName: "editor-translations-nb" */ '@atlaskit/emoji/i18n/nb'),
		import(/* webpackChunkName: "editor-translations-nb" */ '@atlaskit/editor-common/i18n/nb'),
		import(/* webpackChunkName: "editor-translations-nb" */ '@atlaskit/media-ui/i18n/nb'),
		import(/* webpackChunkName: "editor-translations-nb" */ '@atlaskit/smart-card/i18n/nb'),
	],
	nl: () => [
		import(/* webpackChunkName: "editor-translations-nl" */ '@atlaskit/editor-core/i18n/nl'),
		import(/* webpackChunkName: "editor-translations-nl" */ '@atlaskit/emoji/i18n/nl'),
		import(/* webpackChunkName: "editor-translations-nl" */ '@atlaskit/editor-common/i18n/nl'),
		import(/* webpackChunkName: "editor-translations-nl" */ '@atlaskit/media-ui/i18n/nl'),
		import(/* webpackChunkName: "editor-translations-nl" */ '@atlaskit/smart-card/i18n/nl'),
	],
	pl: () => [
		import(/* webpackChunkName: "editor-translations-pl" */ '@atlaskit/editor-core/i18n/pl'),
		import(/* webpackChunkName: "editor-translations-pl" */ '@atlaskit/emoji/i18n/pl'),
		import(/* webpackChunkName: "editor-translations-pl" */ '@atlaskit/editor-common/i18n/pl'),
		import(/* webpackChunkName: "editor-translations-pl" */ '@atlaskit/media-ui/i18n/pl'),
		import(/* webpackChunkName: "editor-translations-pl" */ '@atlaskit/smart-card/i18n/pl'),
	],
	pt_BR: () => [
		import(/* webpackChunkName: "editor-translations-pt_BR" */ '@atlaskit/editor-core/i18n/pt_BR'),
		import(/* webpackChunkName: "editor-translations-pt_BR" */ '@atlaskit/emoji/i18n/pt_BR'),
		import(
			/* webpackChunkName: "editor-translations-pt_BR" */ '@atlaskit/editor-common/i18n/pt_BR'
		),
		import(/* webpackChunkName: "editor-translations-pt_BR" */ '@atlaskit/media-ui/i18n/pt_BR'),
		import(/* webpackChunkName: "editor-translations-pt_BR" */ '@atlaskit/smart-card/i18n/pt_BR'),
	],
	pt_PT: () => [
		import(/* webpackChunkName: "editor-translations-pt_PT" */ '@atlaskit/editor-core/i18n/pt_PT'),
		import(/* webpackChunkName: "editor-translations-pt_PT" */ '@atlaskit/emoji/i18n/pt_PT'),
		import(
			/* webpackChunkName: "editor-translations-pt_PT" */ '@atlaskit/editor-common/i18n/pt_PT'
		),
		import(/* webpackChunkName: "editor-translations-pt_PT" */ '@atlaskit/media-ui/i18n/pt_PT'),
		import(/* webpackChunkName: "editor-translations-pt_PT" */ '@atlaskit/smart-card/i18n/pt_PT'),
	],
	ro: () => [
		// Mirrors confluence/next/packages/i18n/src/messages.ts: omit
		// @atlaskit/editor-common and @atlaskit/media-ui — neither ships `ro`.
		import(/* webpackChunkName: "editor-translations-ro" */ '@atlaskit/editor-core/i18n/ro'),
		import(/* webpackChunkName: "editor-translations-ro" */ '@atlaskit/emoji/i18n/ro'),
		import(/* webpackChunkName: "editor-translations-ro" */ '@atlaskit/smart-card/i18n/ro'),
	],
	ru: () => [
		import(/* webpackChunkName: "editor-translations-ru" */ '@atlaskit/editor-core/i18n/ru'),
		import(/* webpackChunkName: "editor-translations-ru" */ '@atlaskit/emoji/i18n/ru'),
		import(/* webpackChunkName: "editor-translations-ru" */ '@atlaskit/editor-common/i18n/ru'),
		import(/* webpackChunkName: "editor-translations-ru" */ '@atlaskit/media-ui/i18n/ru'),
		import(/* webpackChunkName: "editor-translations-ru" */ '@atlaskit/smart-card/i18n/ru'),
	],
	sk: () => [
		import(/* webpackChunkName: "editor-translations-sk" */ '@atlaskit/editor-core/i18n/sk'),
		import(/* webpackChunkName: "editor-translations-sk" */ '@atlaskit/emoji/i18n/sk'),
		import(/* webpackChunkName: "editor-translations-sk" */ '@atlaskit/editor-common/i18n/sk'),
		import(/* webpackChunkName: "editor-translations-sk" */ '@atlaskit/media-ui/i18n/sk'),
		import(/* webpackChunkName: "editor-translations-sk" */ '@atlaskit/smart-card/i18n/sk'),
	],
	sl: () => [
		import(/* webpackChunkName: "editor-translations-sl" */ '@atlaskit/editor-core/i18n/sl'),
		import(/* webpackChunkName: "editor-translations-sl" */ '@atlaskit/emoji/i18n/sl'),
		import(/* webpackChunkName: "editor-translations-sl" */ '@atlaskit/editor-common/i18n/sl'),
		import(/* webpackChunkName: "editor-translations-sl" */ '@atlaskit/media-ui/i18n/sl'),
		import(/* webpackChunkName: "editor-translations-sl" */ '@atlaskit/smart-card/i18n/sl'),
	],
	// No `sr` / `sr_RS` / `sr_YR` entries — Serbian is intentionally
	// unsupported here, mirroring confluence/next/packages/i18n/src/messages.ts.
	sv: () => [
		import(/* webpackChunkName: "editor-translations-sv" */ '@atlaskit/editor-core/i18n/sv'),
		import(/* webpackChunkName: "editor-translations-sv" */ '@atlaskit/emoji/i18n/sv'),
		import(/* webpackChunkName: "editor-translations-sv" */ '@atlaskit/editor-common/i18n/sv'),
		import(/* webpackChunkName: "editor-translations-sv" */ '@atlaskit/media-ui/i18n/sv'),
		import(/* webpackChunkName: "editor-translations-sv" */ '@atlaskit/smart-card/i18n/sv'),
	],
	th: () => [
		import(/* webpackChunkName: "editor-translations-th" */ '@atlaskit/editor-core/i18n/th'),
		import(/* webpackChunkName: "editor-translations-th" */ '@atlaskit/emoji/i18n/th'),
		import(/* webpackChunkName: "editor-translations-th" */ '@atlaskit/editor-common/i18n/th'),
		import(/* webpackChunkName: "editor-translations-th" */ '@atlaskit/media-ui/i18n/th'),
		import(/* webpackChunkName: "editor-translations-th" */ '@atlaskit/smart-card/i18n/th'),
	],
	tr: () => [
		import(/* webpackChunkName: "editor-translations-tr" */ '@atlaskit/editor-core/i18n/tr'),
		import(/* webpackChunkName: "editor-translations-tr" */ '@atlaskit/emoji/i18n/tr'),
		import(/* webpackChunkName: "editor-translations-tr" */ '@atlaskit/editor-common/i18n/tr'),
		import(/* webpackChunkName: "editor-translations-tr" */ '@atlaskit/media-ui/i18n/tr'),
		import(/* webpackChunkName: "editor-translations-tr" */ '@atlaskit/smart-card/i18n/tr'),
	],
	uk: () => [
		import(/* webpackChunkName: "editor-translations-uk" */ '@atlaskit/editor-core/i18n/uk'),
		import(/* webpackChunkName: "editor-translations-uk" */ '@atlaskit/emoji/i18n/uk'),
		import(/* webpackChunkName: "editor-translations-uk" */ '@atlaskit/editor-common/i18n/uk'),
		import(/* webpackChunkName: "editor-translations-uk" */ '@atlaskit/media-ui/i18n/uk'),
		import(/* webpackChunkName: "editor-translations-uk" */ '@atlaskit/smart-card/i18n/uk'),
	],
	vi: () => [
		import(/* webpackChunkName: "editor-translations-vi" */ '@atlaskit/editor-core/i18n/vi'),
		import(/* webpackChunkName: "editor-translations-vi" */ '@atlaskit/emoji/i18n/vi'),
		import(/* webpackChunkName: "editor-translations-vi" */ '@atlaskit/editor-common/i18n/vi'),
		import(/* webpackChunkName: "editor-translations-vi" */ '@atlaskit/media-ui/i18n/vi'),
		import(/* webpackChunkName: "editor-translations-vi" */ '@atlaskit/smart-card/i18n/vi'),
	],
	zh: () => [
		// Both `zh-CN` and `zh-TW` normalise to `zh` via `getLanguageFromLocale`.
		import(/* webpackChunkName: "editor-translations-zh" */ '@atlaskit/editor-core/i18n/zh'),
		import(/* webpackChunkName: "editor-translations-zh" */ '@atlaskit/emoji/i18n/zh'),
		import(/* webpackChunkName: "editor-translations-zh" */ '@atlaskit/editor-common/i18n/zh'),
		import(/* webpackChunkName: "editor-translations-zh" */ '@atlaskit/media-ui/i18n/zh'),
		import(/* webpackChunkName: "editor-translations-zh" */ '@atlaskit/smart-card/i18n/zh'),
	],
};

export const getEditorTranslationImports = (locale: string): Promise<EditorMessageModule>[] => {
	const importer = EDITOR_TRANSLATION_IMPORTERS[locale] ?? EDITOR_TRANSLATION_IMPORTERS.en;
	return importer();
};
